// @ts-check
import {Modal, TextField, Toggle} from '@/components/atoms'
import {useProductCombo} from '@/context/ProductCombo/ProductComboContext'
import {useComboActions} from '@/hooks/productCombo/useComboActions'
import {useIsComboNameUnique} from '@/hooks/productCombo/useIsComboNameUnique'
import {MODAL_TYPE} from '@/utils/constants/globalStyle'
import {
  COMBO_LIMIT_MAX,
  getProductComboSetupErrors,
  isNumberWithinRange,
} from '@/utils/productCombo/productComboValidation'
import {Font} from 'pepsico-ds'
import {useEffect, useState} from 'react'

const COMBO_LIMIT_MIN = 1

export const ComboSetupSection = () => {
  const {productCombo, updateProductCombo, isDuplicating} = useProductCombo()
  const {name, comboLimit, active, productId} = productCombo
  const isNameUnique = useIsComboNameUnique(name, productId)
  const hasComboLimitValue = comboLimit !== null && comboLimit !== undefined
  const [comboLimitInput, setComboLimitInput] = useState(hasComboLimitValue ? String(comboLimit) : '')
  const [hasInvalidComboLimitDraft, setHasInvalidComboLimitDraft] = useState(false)
  const [showInvalidStatusModal, setShowInvalidStatusModal] = useState(false)
  const {getProductStatus} = useComboActions()

  const errors = getProductComboSetupErrors(productCombo, isNameUnique)
  const comboLimitError = comboLimitInput ? isNumberWithinRange(comboLimitInput, COMBO_LIMIT_MIN, COMBO_LIMIT_MAX) : ''
  const keepDuplicatingActiveInvalid = !getProductStatus() && isDuplicating

  useEffect(() => {
    if (hasInvalidComboLimitDraft) {
      return
    }

    setComboLimitInput(hasComboLimitValue ? String(comboLimit) : '')
  }, [comboLimit, hasComboLimitValue, hasInvalidComboLimitDraft])

  const handleComboLimitChange = value => {
    setComboLimitInput(value)

    if (value === '') {
      setHasInvalidComboLimitDraft(false)
      updateProductCombo({comboLimit: null})
      return
    }

    if (/^[+-]?\d+$/.test(value.trim())) {
      setHasInvalidComboLimitDraft(false)
      updateProductCombo({comboLimit: Number(value.trim())})
      return
    }

    setHasInvalidComboLimitDraft(true)
    updateProductCombo({comboLimit: null})
  }

  const updateActiveStatus = value => {
    if (keepDuplicatingActiveInvalid) {
      setShowInvalidStatusModal(true)
    }

    updateProductCombo({active: value})
  }

  const handleCloseModal = () => {
    setShowInvalidStatusModal(false)

    updateProductCombo({active: false})
  }

  return (
    <div className="flex flex-col gap-6 rounded-lg bg-gray-850 p-6">
      <Font variant="bold">Combo Setup</Font>
      <div className="flex gap-6">
        <TextField
          label="Combo Name*"
          value={name}
          updateValue={value => updateProductCombo({name: value})}
          error={name ? errors.name : undefined}
          uiSchema={{placeholder: 'Product Combo Name', className: 'w-[350px]'}}
        />
        <TextField
          label="Combo Limit*"
          value={comboLimitInput}
          updateValue={handleComboLimitChange}
          error={comboLimitError || (!comboLimitInput && hasComboLimitValue ? errors.comboLimit : undefined)}
          uiSchema={{placeholder: 'Combo Limit', className: 'w-[350px]'}}
          type="text"
        />
      </div>
      <div className="h-px bg-gray-600" />
      <Font variant="bold">Availablity</Font>
      <Toggle
        value={active}
        updateValue={value => updateActiveStatus(value)}
        uiSchema={{size: 'small', label: 'Active'}}
      />
      <Modal
        type={MODAL_TYPE.SMALL}
        showModal={showInvalidStatusModal}
        title="This combo product has one or more inactive products associated to it."
        primaryButtonText="Ok"
        classNameWrapper="min-h-80"
        onClickPrimaryButton={handleCloseModal}
        hideCloseButton={true}
      />
    </div>
  )
}
